import Component from 'core/Component';
import { Event } from 'services/EventEmitter';
import { on, off } from 'toolbox/event';
import { mediaQuery } from 'toolbox/mediaQuery';
import { deepMerge } from 'toolbox/deepMerge';
import { mix } from 'core/mixwith';
import LoaderMixin from 'mixins/Loader';
import Accessibility from 'mixins/Accessibility';

/**
 * This is a description of the Refinements constructor function.
 * @class
 * @classdesc This is a description of the Refinements class.
 * @extends Component
 */
export default class Refinements extends mix(Component).with(LoaderMixin, Accessibility) {
    /**
     * Constructor of the class that mainly merge the options of the components
     *
     * @param {HTMLElement} element HTMLElement of the component
     * @param {Object} options That belongs to the component
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            templateName: 'search/refinements',
            url: '',
            resetUrl: '',
            appliedFiltersQty: 0,
            appliedRefinementUrl: null,
            dataModelId: 'refinements',
            trackFocusedElements: true, // enable focused element tracking
            events: {
                toggle: 'search.refinementsPanel.toggle',
                select: 'search.refinement.selected',
                updated: 'search.updated',
                apply: 'search.refinements.apply',
                removeLoader: 'search.removeLoader',
            },
            urlParams: {
                components: 'search_refinements',
            },
            isSlidingPanel: false,
            retainPanelOnReset: false, // retain panel open on reset
        }, options));
    }

    /**
     * All selectors must be cached. Never cache elements that are out of the component scope
     */
    initCache() {
        this.selectors.backButton = this.element.querySelectorAll('[data-js-refinements-back]');
        this.selectors.resetButton = this.element.querySelectorAll('[data-js-refinements-reset]');
        this.selectors.applyButton = this.element.querySelectorAll('[data-js-refinements-apply]');
        this.filterOptions = {};
    }

    /**
     * Init the different state of the component
     * It helps to avoid heavy DOM manipulation
     */
    initState() {
        this.state.loading = false;
        this.currentRefinementUrl = this.options.url;
        this.resetRefinementsUrl = this.options.resetUrl;
        this.appliedFiltersQty = this.options.appliedFiltersQty;
        this.appliedRefinementUrl = this.options.appliedRefinementUrl;
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonymous function
     */
    bindEvents() {
        super.bindEvents();

        on('click', this.selectors.backButton, this.onBackClick.bind(this));
        on('click', this.selectors.applyButton, this.onApplyClick.bind(this));
        on('click', this.selectors.resetButton, this.onResetClick.bind(this));
        on('keydown', this.element, this.onKeyboardControl.bind(this));
        on('pricerangeslider.initialized', this.element, this.onPriceRangeSliderInitialized.bind(this));

        Event.on(this.options.events.select, this.onRefinementSelected, this);
        Event.on(this.options.events.updated, this.onRefinementsUpdated, this);
        Event.on(this.options.events.removeLoader, this.onSearchRemoveLoader, this);
    }

    /**
     * After init
     * Run any script after the component is fully initialized
     */
    afterInit() {
        this.restoreLastFocusState();
    }

    /**
     * Restores last focus state
     * @listens pricerangeslider.initialized
     */
    onPriceRangeSliderInitialized() {
        this.restoreLastFocusState();
    }

    /**
     * Event handler for back button
     * emit an event to close the search panel
     */
    onBackClick() {
        this.closePanel();
        if (mediaQuery.is('medium down')) {
            this.appliedFiltersQty = 0;
            // reset refinements panel to initial state
            this.updateRefinements(this.appliedRefinementUrl || window.location.href);
        }
    }

    /**
     * Event handler for Apply button click
    */
    onApplyClick() {
        this.closePanel();
        this.updateResults();
    }

    /**
     * Close refinements panel
     */
    closePanel() {
        Event.emit(this.options.events.toggle);
        if (this.options.isSlidingPanel) {
            Event.emit('slidingPanel.close');
        }
    }

    /**
     * Close refinements panel
     */
    close() {
        this.onBackClick();
    }

    /**
     * Sets a query parameter with a specified value in a URL string.
     * @param {string} url - The URL to modify.
     * @param {string} paramName - The name of the parameter to set.
     * @param {string} paramValue - The value to set for the parameter.
     * @returns {string} - The modified URL with the updated parameter.
     */
    setUrlParam(url, paramName, paramValue) {
        const urlObj = new URL(url);
        const params = urlObj.searchParams;
        params.set(paramName, paramValue);
        return urlObj.toString();
    }

    /**
     * Gets the value of a query parameter from a URL string.
     * @param {string} url - The URL to extract the parameter from.
     * @param {string} paramName - The name of the parameter to get.
     * @returns {string|null} - The value of the parameter, or null if not found.
     */
    getUrlParam(url, paramName) {
        const urlObj = new URL(url);
        const params = urlObj.searchParams;
        return params.get(paramName);
    }

    /**
     * Event handler for Reset button
     * clear refinements
     */
    onResetClick() {
        if (this.appliedFiltersQty === 0 || this.currentRefinementUrl === this.resetRefinementsUrl) {
            return;
        }

        const sortValue = this.getUrlParam(this.currentRefinementUrl, 'sort');
        const orderValue = this.getUrlParam(this.currentRefinementUrl, 'order');

        if (sortValue && orderValue) {
            this.resetRefinementsUrl = this.setUrlParam(this.resetRefinementsUrl, 'sort', sortValue);
            this.resetRefinementsUrl = this.setUrlParam(this.resetRefinementsUrl, 'order', orderValue);
        }

        this.currentRefinementUrl = this.resetRefinementsUrl;
        this.appliedRefinementUrl = this.currentRefinementUrl;

        if (mediaQuery.is('medium down') && this.options.retainPanelOnReset) {
            this.addLoader(this.element, { overlay: true });
        }

        this.refine({
            url: this.resetRefinementsUrl,
            appliedFiltersQty: '',
        });

        if (!this.options.retainPanelOnReset) {
            this.closePanel();
        }
    }

    /**
     * Event handler for refinement selection
     *
     * @param {Event} e - refinement selection event
     */
    onRefinementSelected(e) {
        this.currentRefinementUrl = e.url;
        this.lastSelectedRefinement = e.refinementId;
        this.state.loading = true;
        if (mediaQuery.is('large')) {
            this.updateResults();
        } else {
            this.updateRefinements(this.currentRefinementUrl);
        }
    }

    /**
     * Emits partial search update to update refinements
     *
     * @param {String} url - url string to get new refinements data from
     */
    updateRefinements(url) {
        this.addLoader(this.element, { overlay: true });

        this.refine({
            url,
            appliedFiltersQty: this.appliedFiltersQty === 0 ? '' : this.appliedFiltersQty,
            urlParams: this.options.urlParams || {},
            refinementId: this.lastSelectedRefinement,
            refinements: true,
            dontUpdateURL: true,
        });
    }

    /**
     * Apply selected refinements
     */
    updateResults() {
        this.appliedRefinementUrl = this.currentRefinementUrl;
        this.refine({
            url: this.currentRefinementUrl,
            appliedFiltersQty: this.appliedFiltersQty === 0 ? '' : this.appliedFiltersQty,
            refinementId: this.lastSelectedRefinement,
        });
    }

    /**
     * Send an event with data to refine products
     *
     * @param {Object} filterOptions Object with filter options
     */
    refine(filterOptions) {
        Event.emit(this.options.events.apply, Object.assign({}, filterOptions));
    }

    /**
     * Callback on result updated from ajax request.
     * If callback is due to a insertion, there is no refinement to be changed.
     * @param {Object} data Data due to refinement to be changed
     * @param {Boolean} isInsertMode Flag due to a insertion
     */
    onRefinementsUpdated(data, isInsertMode) {
        if (data[this.options.dataModelId] && !isInsertMode) {
            this.saveLastFocusState();
            data[this.options.dataModelId].currentRefinementUrl = this.currentRefinementUrl;
            data[this.options.dataModelId].categoryname = data.categoryname;
            data[this.options.dataModelId].refinebycategory = data.refinebycategory;
            data[this.options.dataModelId].componentOptions = data[this.options.dataModelId].componentOptions || {};
            data[this.options.dataModelId].componentOptions.appliedRefinementUrl = this.appliedRefinementUrl;
            this.removeLoader(this.element);
            this.render(data[this.options.dataModelId]);
        }
    }

    /**
     * Tab key event
     *
     * @param {Object} event Event object
    */
    onTabKey(event) {
        if (mediaQuery.is('medium down')) {
            super.focusTrap(this.element, event);
        }
    }

    /**
     * Keydown event handler
     *
     * @param {Object} event Event object
     */
    onKeyboardControl(event) {
        switch (event.key) {
        case 'Tab': // 'Tab' key
            this.onTabKey(event);
            break;
        case 'Escape': // 'Escape' key
            this.close();
            break;
        default:
            break;
        }
    }

    /**
     * Event handler for remove loader
     */
    onSearchRemoveLoader() {
        this.removeLoader(this.element);
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        super.destroy();

        off('click', this.selectors.backButton);
        off('click', this.selectors.resetButton);
        off('click', this.selectors.applyButton);
        off('keydown', this.element);
        Event.removeListener(this.options.events.select, this.onRefinementSelected, this);
        Event.removeListener(this.options.events.updated, this.onRefinementsUpdated, this);
        Event.removeListener(this.options.events.removeLoader, this.onSearchRemoveLoader, this);
    }
}
